import React, { useContext, useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useParams, Link } from "react-router-dom";
import MicroModal from "micromodal";

import { ASSETS_URL, librosMaxDescargados, store } from "../../components/Store";
import { ModoPWAContext } from "../../contexts/ModoPwaContext";

import { Preview } from "./preview";
import { Cover } from "../Cover";
import { Activity } from "./activity";
import AlertaLimite from "../AlertaLimite";

import { useBookData } from "../useBookData";
import { useBookDownloader } from "../../offline/hooks/use-book-downloader";
import { useDownloadedBooksManager } from "../../offline/hooks/use-downloaded-books-manager";
import { useFavoritesManager } from "../../hooks/use-favorites-manager";
import useIsMobile from "../../js/utilities/useIsMobile";

/**
 * Componente que el area de la portada en la ficha del libro
 * @param {object} metada - informacion del libro 
 * @param {object} textoFicha - textos de interfaz
 * @returns CoverSection
 */
export const CoverSection = ({ metadata, textoFicha }) => {
    const globalState = useContext(store);
    const { state } = globalState;
    const pwaState = useContext(ModoPWAContext);
    const [idLibro, setIdLibro] = useState("");
    const [descargado, setDescargado] = useState(false);
    const [urlLibro, setUrlLibro] = useState("");
    const params = useParams();
    const isMobile = useIsMobile();
    const userBooks = state?.userBooksData;

    let book = useBookData(params?.idLibro);
    const downloadedBooksManager = useDownloadedBooksManager();
    const downloadedBooks = downloadedBooksManager?.books;
    const downloadedBooksLength = downloadedBooks?.length;

    const isLogged = state.sesionActiva !== null && state.sesionActiva === "SI";

    const isBookFromUserBooks = useMemo(() => {
        return userBooks?.books?.map(book => book?.idLibro)?.includes(book?.id);
    }, [userBooks?.books, book?.id]);

    const bookAvailable = (metadata?.territorio && typeof metadata?.territorio === "string" && metadata?.territorio?.trim()?.length > 0);


    //Libro favorito
    const favoritesManager = useFavoritesManager();
    const favorites = favoritesManager?.favoriteBooks;
    const isFavorite = favorites?.some((b) => b?.idLibro === params?.idLibro);
    //Datos descarga
    const [alertaLimite, setAlertaLimite] = useState(false);

    const previewPages = [metadata.screen1, metadata.screen2, metadata.screen3];

    useEffect(() => {
        setIdLibro(params.idLibro);
    }, [params.idLibro]);

    //Url libro: actualiza el estado urlLibro
    useEffect(() => {
        let href;
        if (isLogged && !isBookFromUserBooks) {
            setUrlLibro('#')
            return;
        }
        if (isLogged && state.cantidadLibros === 0) {
            href = "/mi-panel/mi-coleccion";
        }
        else if (isLogged && state.cantidadLibros > 0) {
            href = `/visor/${params?.idLibro}`;
        }
        else if (!isLogged && book?.metadata?.gratis === 1) {
            href = `/visor/${params?.idLibro}`;
        }
        else {
            href = `/login?bookT=/visor/${params?.idLibro}/`;
        }
        setUrlLibro(href);
    }, [book, isLogged, params.idLibro, state.cantidadLibros, isBookFromUserBooks])


    /** Agrega o elimina el libro los favoritos en el localstorage*/
    const agregarRemoverAFavoritos = () => {
        if (isFavorite) {
            favoritesManager.remove({bookId: idLibro});
        } else {
            favoritesManager.add({bookId: idLibro});
        }
    }

    const bookDownloader = useBookDownloader({
        book, onSuccess: () => {
            setDescargado(true);
        }
    });

    const handleDownload = () => {
        bookDownloader.download();
        if (downloadedBooksLength >= librosMaxDescargados) {
            setAlertaLimite(true);
            return;
        }
    }

    useEffect(() => {
        if (Array.isArray(downloadedBooks)) {
            const isDownloaded = downloadedBooks?.find(book => book?.bookId === params.idLibro);
            setDescargado(isDownloaded);
        }
    }, [downloadedBooks, params.idLibro])
    

    return (
        <>
            {alertaLimite && (
                <AlertaLimite setAlertaLimite={setAlertaLimite} />
            )}

            <aside className="mb-4 mb-lg-0 px-0 px-lg-3 cover-section">
                <div className="cover-container">
                    {isMobile &&
                        <Activity metadata={metadata} textoFicha={textoFicha} />
                    }
                    <Cover metadata={{ ...metadata, portada: `${ASSETS_URL}${metadata?.portada}` }} urlLibro={urlLibro} />


                    {/* leer */}
                    {
                        ((isLogged && isBookFromUserBooks) || (!isLogged && bookAvailable)) &&
                        <Link
                            to={urlLibro}
                            className="focusable-red buttonv2 button-primary mt-4 focusable-red px-5 mx-auto d-block col-sm-10 btn-leer"
                            style={{ maxWidth: 285 }}
                            onClick={(e) => {
                                if (metadata?.limiteDeLecturasAlcanzado) {
                                    e.preventDefault();
                                    MicroModal.show("modal-alerts", {
                                        awaitCloseAnimation: true,
                                    });
                                }
                            }}
                        >
                            {textoFicha?.botones?.leer || 'Leer'}
                            <Icon icon="tabler:chevron-right" width={20} className="ml-2" />
                        </Link>
                    }
                </div>


                <div className="favorite-box d-flex d-lg-block pt-4 pt-lg-0">

                    {/* favorito */}
                    {
                        isLogged && isBookFromUserBooks &&
                        <div className="col-6 col-lg-12 mb-0 mb-lg-4">
                            <button
                                className="mt-lg-3 btn-favorito focusable-secondary body-m body-lg-base"
                                onClick={() => agregarRemoverAFavoritos()}
                            >
                                <div className="icon-opc">
                                    {isFavorite ?
                                        <Icon icon="material-symbols:favorite" className="red-inverse--color" width={25} />
                                        :
                                        <Icon icon="material-symbols:favorite-outline" width={25} />
                                    }
                                </div>

                                <p className="d-md-none">{textoFicha?.botones?.favorito_res || 'Favorito'}</p>
                                <p className="d-none d-md-block">{textoFicha?.botones?.favorito || 'Marcar como favorito'}</p>

                            </button>
                        </div>
                    }

                    {/* ver unas paginas */}
                    {
                        (isLogged || (!isLogged && bookAvailable)) &&

                        <div className={`${isLogged ? "col-preview col-6 col-lg-12 px-0" : "col-12"}`}>
                            <Preview
                                texts={textoFicha}
                                titleBook={metadata.titulo}
                                previewPages={previewPages}
                                isLogged={isLogged}
                                isBookFromUserBooks={isBookFromUserBooks}
                            >
                                {
                                    ((isLogged && isBookFromUserBooks) || (!isLogged && bookAvailable)) &&
                                    <Link
                                        to={urlLibro}
                                        className="btn btn-primary secondary-font focusable-red px-5 d-block col-sm-10 col-lg-9"
                                        onClick={(e) => {
                                            if (metadata?.limiteDeLecturasAlcanzado) {
                                                e.preventDefault();
                                                MicroModal.show("modal-alerts", {
                                                    awaitCloseAnimation: true,
                                                });
                                            }
                                        }}
                                    >
                                        {textoFicha?.botones?.leer || 'Leer'}
                                    </Link>
                                }
                            </Preview>
                        </div>
                    }
                </div>



                <div className="mt-4 buttons-box">
                    {
                        isLogged && isBookFromUserBooks && bookAvailable && pwaState?.clienteHabilitado && pwaState?.compatible &&
                        <>
                            {(() => {
                                if (descargado) {
                                    return (
                                        <Link to={'/descargados'} className="button-option-book body-base focusable-secondary" aria-label="libro descargado" disabled>
                                            {textoFicha?.botones?.descargado || 'Descargado'}
                                            <Icon icon="tabler:check" width={15} height={15} className="ml-1" />
                                        </Link>
                                    );
                                } else if (alertaLimite) {
                                    return (
                                        <button
                                            className="button-option-book body-base focusable-secondary"
                                            disabled
                                        >
                                            {textoFicha?.botones?.descargar || 'Descargar'}
                                            <Icon icon="tabler:book-download" width={15} height={15} className="ml-1" />
                                        </button>
                                    );
                                } else {
                                    return (
                                        <button
                                            className="button-option-book body-base focusable-secondary"
                                            disabled={state.descargando}
                                            onClick={() => handleDownload()}
                                        >
                                            {textoFicha?.botones?.descargar || 'Descargar'}
                                            <Icon icon="tabler:book-download" width={15} height={15} className="ml-1" />
                                        </button>
                                    );
                                }
                            })()}
                        </>
                    }

                    {
                        isLogged &&
                        <button
                            className="button-option-book body-base focusable-red"
                            onClick={() => {
                                MicroModal.show('modal-compartir-libro', {
                                    awaitCloseAnimation: true
                                });
                            }}
                        >
                            {textoFicha?.botones?.compartir || 'Compartir'}
                            <Icon icon="ri:share-line" width="18" height="18" className="ml-1" />
                        </button>
                    }
                </div>

            </aside>
        </>
    )
}